import { createPortal } from 'react-dom';
import { X, Heart } from 'lucide-react';
import type { DonationBlock } from '@/types/donation';

// ============================================
// TYPES
// ============================================

interface DonationMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  blocks: DonationBlock[];
  title?: string;
}

// ============================================
// BLOCK RENDERERS
// ============================================

interface ImageBlockProps {
  block: DonationBlock;
}

function ImageBlock({ block }: ImageBlockProps) {
  const handleClick = () => {
    if (block.imageLinkUrl) {
      window.open(block.imageLinkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={block.imageLinkUrl ? 'cursor-pointer hover:opacity-95 transition-opacity' : ''}
      onClick={block.imageLinkUrl ? handleClick : undefined}
    >
      <img src={block.imageUrl} alt={block.imageAlt || ''} className="w-full rounded-lg" />
    </div>
  );
}

interface TextBlockProps {
  block: DonationBlock;
}

function TextBlock({ block }: TextBlockProps) {
  return (
    <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">{block.textContent}</div>
  );
}

interface ButtonBlockProps {
  block: DonationBlock;
}

function ButtonBlock({ block }: ButtonBlockProps) {
  const handleClick = () => {
    if (block.buttonUrl) {
      window.open(block.buttonUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="text-center">
      <button
        type="button"
        onClick={handleClick}
        className="inline-block px-8 py-3 bg-gradient-to-r from-[#8e4585] to-[#a855a0] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:opacity-95 transition-all active:scale-98"
      >
        {block.buttonText || 'Donate Now'}
      </button>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function DonationMessageModal({
  isOpen,
  onClose,
  blocks,
  title = 'Support Our Mission',
}: DonationMessageModalProps) {
  if (!isOpen) return null;

  // Sort blocks by order
  const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order);

  const modalContent = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
        style={{
          marginTop: 'var(--safe-area-top, 0px)',
          marginBottom: 'var(--safe-area-bottom, 0px)',
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#8e4585] to-[#63345e] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 text-white">
            <Heart size={20} fill="currentColor" />
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {sortedBlocks.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Heart size={48} className="mx-auto text-slate-300 mb-4" />
              <p>Thank you for considering a donation!</p>
            </div>
          ) : (
            sortedBlocks.map((block) => {
              switch (block.type) {
                case 'image':
                  return block.imageUrl ? <ImageBlock key={block.id} block={block} /> : null;
                case 'text':
                  return block.textContent ? <TextBlock key={block.id} block={block} /> : null;
                case 'button':
                  return block.buttonText ? <ButtonBlock key={block.id} block={block} /> : null;
                default:
                  return null;
              }
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 shrink-0 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors text-center"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level, ensuring proper z-index stacking
  return createPortal(modalContent, document.body);
}
